import { supabase, ensureDiscoverySession, toError } from './supabase';
import type { Category } from './database.client.types';
import { withE2ETestTag } from './e2eTestRun';
import {
    clearEntitlementSnapshotCache,
    isProjectLimitReachedError,
    preflightProjectCreation,
    toProjectLimitReachedError,
} from './entitlements';
import { canAccessMainApp } from '../utils/authHelpers';
import {
    canAccessPubmed,
    fetchCurrentUserSourceAccess,
    isSourceEntitlementRequiredError,
    PUBMED_SOURCE_ID,
    SOURCE_ENTITLEMENT_ERROR_CODE,
    type SourceAccessDecision,
} from './sourceAccess';

// Auto-generated project name format - centralized so format changes only need one update
const AUTO_GENERATED_NAME_PREFIX = 'Digest - ';

export function generateAutoProjectName(): string {
    return withE2ETestTag(`${AUTO_GENERATED_NAME_PREFIX}${new Date().toISOString()}`);
}

export function isAutoGeneratedProjectName(name: string | null | undefined): boolean {
    if (!name) return true;
    // Check if it starts with the prefix followed by an ISO timestamp pattern
    if (!name.startsWith(AUTO_GENERATED_NAME_PREFIX)) return false;
    const datePart = name.slice(AUTO_GENERATED_NAME_PREFIX.length);
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(datePart);
}

export interface CreateProjectParams {
    projectName?: string;
    interestText: string;
    categories: Category[];
    includeKeywords: string[];
    excludeKeywords: string[];
}

export interface CreateProjectResult {
    projectId: string;
    projectName: string;
}

export interface UpdateProjectParams {
    projectId: string;
    interestText: string;
    categories: Category[];
    includeKeywords: string[];
    excludeKeywords: string[];
}

export async function createDiscoveryProject(params: CreateProjectParams): Promise<CreateProjectResult> {
    const { projectName, interestText, categories, includeKeywords, excludeKeywords } = params;

    try {
        // Ensure we have a session (create anonymous if needed)
        const { session } = await ensureDiscoverySession();

        // Validate session exists before proceeding
        if (!session || !session.user?.id) {
            throw new Error('Failed to establish session. Cannot create project without authentication.');
        }

        if (categories.some((category) => category.source_id === PUBMED_SOURCE_ID)) {
            let pubmedAllowed = false;
            try {
                const decisions = await fetchCurrentUserSourceAccess();
                pubmedAllowed = canAccessPubmed(decisions);
            } catch (error) {
                console.warn('⚠️ [Discovery Project Service] PubMed access could not be verified:', error);
            }
            if (!pubmedAllowed) {
                throw new Error(SOURCE_ENTITLEMENT_ERROR_CODE);
            }
        }

        if (session.access_token && canAccessMainApp(session)) {
            await preflightProjectCreation(session.access_token);
        }

        console.log('📍 [Discovery Project Service] Session ready:', {
            userId: session.user.id,
            isAnonymous: !session.user?.email,
            hasEmail: !!session.user?.email,
            useLocal: process.env.EXPO_PUBLIC_USE_LOCAL_SUPABASE === 'true',
            supabaseUrl: process.env.EXPO_PUBLIC_USE_LOCAL_SUPABASE === 'true'
                ? process.env.EXPO_PUBLIC_SUPABASE_DEV_URL
                : process.env.EXPO_PUBLIC_SUPABASE_URL
        });

        // Test if user has basic SELECT permissions on project table
        try {
            const { count, error: testError } = await supabase
                .from('project')
                .select('*', { count: 'exact', head: true })
                .limit(1);

            console.log('🔍 [Discovery Project Service] Project table access test:', {
                success: !testError,
                count,
                error: testError
            });
        } catch (testErr) {
            console.error('🔍 [Discovery Project Service] Project table access test failed:', testErr);
        }

        const normalizedProjectName = projectName?.trim();
        const initialProjectName = normalizedProjectName && normalizedProjectName.length >= 3
            ? withE2ETestTag(normalizedProjectName.slice(0, 120))
            : generateAutoProjectName();

        // Create the project using local Supabase in draft mode
        const { data: project, error: projectError } = await supabase
            .from('project')
            .insert({
                name: initialProjectName,
                interest_description: interestText.trim(),
                user_id: session.user.id,
                email_frequency: 'daily',
                mode: 'draft',
            })
            .select()
            .single();

        if (projectError) {
            console.error('❌ [Discovery Project Service] Error creating project:', {
                error: projectError,
                code: projectError.code,
                message: projectError.message,
                details: projectError.details,
                hint: projectError.hint,
                userId: session.user.id,
                isAnonymous: !session.user?.email,
                useLocal: process.env.EXPO_PUBLIC_USE_LOCAL_SUPABASE === 'true'
            });

            if (projectError.code === '42501' || projectError.message?.includes('permission denied')) {
                throw new Error(`Permission denied: Anonymous users may not have INSERT permissions on project table. Error: ${projectError.message}`);
            }

            if (isProjectLimitReachedError(projectError)) {
                throw toProjectLimitReachedError(projectError);
            }

            throw toError(projectError);
        }

        // Insert categories if any
        if (categories.length > 0) {
            const { error: catError } = await supabase
                .from('project_category_interests')
                .insert(
                    categories.map(cat => ({
                        project_id: project.id,
                        category_id: cat.id,
                    }))
                );
            if (catError) {
                if (isSourceEntitlementRequiredError(catError)) {
                    throw new Error(SOURCE_ENTITLEMENT_ERROR_CODE);
                }
                throw toError(catError);
            }
        }

        // Insert keywords if any
        const keywordInserts = [];

        if (includeKeywords.length > 0) {
            keywordInserts.push(...includeKeywords.map(keyword => ({
                project_id: project.id,
                keyword,
                is_exclusion: false,
                match_type: 'both' as const,
            })));
        }

        if (excludeKeywords.length > 0) {
            keywordInserts.push(...excludeKeywords.map(keyword => ({
                project_id: project.id,
                keyword,
                is_exclusion: true,
                match_type: 'both' as const,
            })));
        }

        if (keywordInserts.length > 0) {
            const { error: keywordError } = await supabase
                .from('project_keyword')
                .insert(keywordInserts);
            if (keywordError) throw toError(keywordError);
        }

        // Update project to experimental mode - signals it's ready for processing
        // and aligns with backend contract to stamp setup completion.
        const setupCompleteAt = new Date().toISOString();
        const { error: updateError } = await supabase
            .from('project')
            .update({
                mode: 'experimental',
                matching_state: 'pending_initial',
                setup_complete_at: setupCompleteAt,
            })
            .eq('id', project.id)
            .is('deleted_at', null);

        if (updateError) throw toError(updateError);

        console.log('🎯 [Discovery Project Service] Project configured for onboarding:', project.id);
        clearEntitlementSnapshotCache();
        return { projectId: project.id, projectName: initialProjectName };

    } catch (error) {
        console.error('❌ [Discovery Project Service] Error creating project:', error);
        if (isProjectLimitReachedError(error)) {
            throw toProjectLimitReachedError(error);
        }
        throw toError(error);
    }
}

export async function updateDiscoveryProject(params: UpdateProjectParams): Promise<void> {
    const { projectId, interestText, categories, includeKeywords, excludeKeywords } = params;

    try {
        // Ensure we have a session
        const { session } = await ensureDiscoverySession();

        // Validate session exists before proceeding
        if (!session || !session.user?.id) {
            throw new Error('Failed to establish session. Cannot update project without authentication.');
        }

        console.log('📍 [Discovery Project Service] Updating project:', projectId);
        console.log('📊 [Discovery Project Service] Update params:', {
            categoriesCount: categories.length,
            categories: categories.map(c => ({ id: c.id, code: c.code, name: c.name })),
            interestTextLength: interestText.length,
            includeKeywordsCount: includeKeywords.length,
            excludeKeywordsCount: excludeKeywords.length
        });
        let sourceAccess: SourceAccessDecision[] = [];
        try {
            sourceAccess = await fetchCurrentUserSourceAccess(projectId);
        } catch (error) {
            console.warn('⚠️ [Discovery Project Service] Source access lookup failed; PubMed will remain locked:', error);
        }
        const pubmedAllowed = canAccessPubmed(sourceAccess);

        // Step 0: Get current mode to preserve it after update
        const { data: currentProject, error: fetchError } = await supabase
            .from('project')
            .select(`
                mode,
                project_category_interests (
                    id,
                    category:category_id (id, source_id)
                )
            `)
            .eq('id', projectId)
            .is('deleted_at', null)
            .single();

        if (fetchError) throw toError(fetchError);

        const originalMode = currentProject?.mode || 'experimental'; // Default to experimental if not found
        const existingInterests = (currentProject?.project_category_interests || []) as Array<{
            id: number;
            category: { id: number; source_id: number } | null;
        }>;
        const lockedPubmedCategoryIds = new Set(
            existingInterests
                .filter((interest) => interest.category?.source_id === PUBMED_SOURCE_ID && !pubmedAllowed)
                .map((interest) => interest.category!.id)
        );
        const requestedUnauthorizedPubmed = categories.some(
            (category) => category.source_id === PUBMED_SOURCE_ID
                && !pubmedAllowed
                && !lockedPubmedCategoryIds.has(category.id)
        );
        if (requestedUnauthorizedPubmed) {
            throw new Error(SOURCE_ENTITLEMENT_ERROR_CODE);
        }

        // Step 1: Set project to draft mode during update
        const { error: draftError } = await supabase
            .from('project')
            .update({ mode: 'draft' })
            .eq('id', projectId)
            .is('deleted_at', null);

        if (draftError) throw toError(draftError);

        // Step 2: Update project interest description
        const { error: updateError } = await supabase
            .from('project')
            .update({
                interest_description: interestText.trim(),
            })
            .eq('id', projectId)
            .is('deleted_at', null);

        if (updateError) throw toError(updateError);

        // Step 3: Replace only editable interests. Locked PubMed selections are
        // retained verbatim so a downgrade does not destroy project configuration.
        const editableInterestIds = existingInterests
            .filter((interest) => interest.category?.source_id !== PUBMED_SOURCE_ID || pubmedAllowed)
            .map((interest) => interest.id);
        if (editableInterestIds.length > 0) {
            const { error: deleteCatError } = await supabase
                .from('project_category_interests')
                .delete()
                .in('id', editableInterestIds);

            if (deleteCatError) throw toError(deleteCatError);
        }

        // Step 4: Delete existing keywords
        const { error: deleteKeyError } = await supabase
            .from('project_keyword')
            .delete()
            .eq('project_id', projectId);

        if (deleteKeyError) throw toError(deleteKeyError);

        // Step 5: Insert new categories if any
        const categoriesToInsert = categories.filter(
            (category) => category.source_id !== PUBMED_SOURCE_ID || pubmedAllowed
        );
        if (categoriesToInsert.length > 0) {
            const { error: catError } = await supabase
                .from('project_category_interests')
                .insert(
                    categoriesToInsert.map(cat => ({
                        project_id: projectId,
                        category_id: cat.id,
                    }))
                );
            if (catError) {
                if (isSourceEntitlementRequiredError(catError)) {
                    throw new Error(SOURCE_ENTITLEMENT_ERROR_CODE);
                }
                throw toError(catError);
            }
        }

        // Step 6: Insert new keywords if any
        const keywordInserts = [];

        if (includeKeywords.length > 0) {
            keywordInserts.push(...includeKeywords.map(keyword => ({
                project_id: projectId,
                keyword,
                is_exclusion: false,
                match_type: 'both' as const,
            })));
        }

        if (excludeKeywords.length > 0) {
            keywordInserts.push(...excludeKeywords.map(keyword => ({
                project_id: projectId,
                keyword,
                is_exclusion: true,
                match_type: 'both' as const,
            })));
        }

        if (keywordInserts.length > 0) {
            const { error: keywordError } = await supabase
                .from('project_keyword')
                .insert(keywordInserts);
            if (keywordError) throw toError(keywordError);
        }

        // Step 7: Restore original mode and trigger rematch
        // If project was active, keep it active; if experimental, keep it experimental
        const definitionChangedAt = new Date().toISOString();
        const { error: restoreModeError } = await supabase
            .from('project')
            .update({
                mode: originalMode,
                matching_state: 'pending_rematch',
                changed_at: definitionChangedAt,
                definition_changed_at: definitionChangedAt,
            })
            .eq('id', projectId)
            .is('deleted_at', null);

        if (restoreModeError) throw toError(restoreModeError);

        console.log('✅ [Discovery Project Service] Project updated successfully:', projectId, '- mode restored to:', originalMode);

    } catch (error) {
        console.error('❌ [Discovery Project Service] Error updating project:', error);
        if (isSourceEntitlementRequiredError(error)) {
            throw new Error(SOURCE_ENTITLEMENT_ERROR_CODE);
        }
        throw toError(error);
    }
}

export async function activateDiscoveryProject(projectId: string): Promise<void> {
    try {
        // Ensure we have a session
        const { session } = await ensureDiscoverySession();

        // Validate session exists before proceeding
        if (!session || !session.user?.id) {
            throw new Error('Failed to establish session. Cannot activate project without authentication.');
        }

        console.log('🚀 [Discovery Project Service] Activating project for AI boost:', projectId);

        // Update project to active mode - triggers the AI boost pipeline
        const { error } = await supabase
            .from('project')
            .update({
                mode: 'active',
                pending_activation: false  // Clear pending activation flag
            })
            .eq('id', projectId)
            .is('deleted_at', null);

        if (error) throw toError(error);

        // Stamp activation/setup timestamps if missing (legacy-safe, idempotent)
        const activatedAt = new Date().toISOString();
        const { error: activatedAtError } = await supabase
            .from('project')
            .update({ activated_at: activatedAt })
            .eq('id', projectId)
            .is('deleted_at', null)
            .is('activated_at', null);

        if (activatedAtError) throw toError(activatedAtError);

        const { error: setupCompleteAtError } = await supabase
            .from('project')
            .update({ setup_complete_at: activatedAt })
            .eq('id', projectId)
            .is('deleted_at', null)
            .is('setup_complete_at', null);

        if (setupCompleteAtError) throw toError(setupCompleteAtError);

        console.log('✅ [Discovery Project Service] Project activated for AI boost:', projectId);

    } catch (error) {
        console.error('❌ [Discovery Project Service] Error activating project:', error);
        throw toError(error);
    }
}

export async function deleteDiscoveryProject(projectId: string): Promise<void> {
    try {
        // Ensure we have a session
        const { session } = await ensureDiscoverySession();

        // Validate session exists before proceeding
        if (!session || !session.user?.id) {
            throw new Error('Failed to establish session. Cannot delete project without authentication.');
        }

        console.log('🗑️ [Discovery Project Service] Soft deleting project:', projectId);

        // Call the database soft delete function
        const { error } = await supabase.rpc('soft_delete_project', {
            project_id: projectId
        });

        if (error) throw toError(error);

        console.log('✅ [Discovery Project Service] Project soft deleted successfully:', projectId);
        clearEntitlementSnapshotCache();

    } catch (error) {
        console.error('❌ [Discovery Project Service] Error deleting project:', error);
        throw toError(error);
    }
}
